import React, {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {qualificationScopes} from "../../../../db/datasets/qualifications";
import {
    hasCompetence,
    hasJob,
    hasLanguage,
    hasMaterial,
    hasQualification,
    mandatoryItems,
    optionalItems,
    sanitizeUrl
} from "../../../../helpers/misc";
import {professionScopes} from "../../../../db/datasets/professions";
import {languages} from "../../../../db/datasets/languages";
import {competences} from "../../../../db/datasets/competences";
import {selectCv} from "../../../../slices/profile";
import InsertCommentIcon from "@material-ui/icons/InsertComment";
import {competenceLevels, jobContracts, languageLevels} from "../../../../db/models/const";
import ButtonCircle from "../../../../components/Common/ButtonCircle";
import Button from "../../../../components/Common/Button";
import {openAlert} from "../../../../slices/alert";
import {RmMaterialFeedback} from "../../../../components/Dialog/RmMaterialFeedback";
import {closeDialog, openDialog} from "../../../../slices/dialog";
import {Tooltip} from "@material-ui/core";
import {selectSelectedRm} from "../../../../slices/selected";
import {rmBgItemsAsArray} from "../../../../helpers/companyRm";
import {typeIdToTypeLabel} from "../../../../helpers/bgQualifications";
import {useTranslation} from "react-i18next";
import {ForgotPassword} from "../../../../components/CommonComponents";
import {CourseReminder} from "./components/CourseReminder";
import {datasetIdToEntry, datasetIdToLabel} from "../../../../common/helpers/dataset";

var emojiCompact = require('emoji.json/emoji-compact.json');

export const FollowQualifications = ({sectionCounters}) => {
    const rm = useSelector(selectSelectedRm);
    const {qualifications} = rmBgItemsAsArray(rm);
    const mandatoryQualifications = mandatoryItems(qualifications);
    const optionalQualifications = optionalItems(qualifications);
    const {t} = useTranslation();

    return (
        <div className="followQualifications">
            <div className="mandatoryCol">
                <p className="counter">{t("student_follow_mandatory") + " "}<strong>
                    {sectionCounters.mandatory.done}{"/"}
                    {sectionCounters.mandatory.total}
                </strong></p>
                {mandatoryQualifications.length > 0 &&
                    <div className="card">
                        {mandatoryQualifications.map(qualification =>
                            <FollowQualification qualification={qualification}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[3741]}</p>
                        </div>
                    </div>
                }
            </div>
            <div className="optionalCol">
                <p className="counter">{t("student_follow_optionals") + " "}<strong>
                    {sectionCounters.optional.done}{"/"}
                    {sectionCounters.optional.total}
                </strong></p>
                {optionalQualifications.length > 0 &&
                    <div className="card">
                        {optionalQualifications.map(qualification => <FollowQualification
                            qualification={qualification}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[3741]}</p>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export const FollowJobs = ({sectionCounters}) => {
    const rm = useSelector(selectSelectedRm);
    const {jobs} = rmBgItemsAsArray(rm);
    const mandatoryJobs = mandatoryItems(jobs);
    const optionalJobs = optionalItems(jobs);
    const {t} = useTranslation();

    return (
        <div className="followJobs">
            <div className="mandatoryCol">
                <p className="counter">{t("student_follow_mandatory") + " "}<strong>
                    {sectionCounters.mandatory.done}{"/"}
                    {sectionCounters.mandatory.total}
                </strong></p>
                {mandatoryJobs.length > 0 &&
                    <div className="card">
                        {mandatoryJobs.map(job => <FollowJob job={job}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[3894]}</p>
                        </div>
                    </div>
                }
            </div>
            <div className="optionalCol">
                <p className="counter">{t("student_follow_optionals") + " "}<strong>
                    {sectionCounters.optional.done}{"/"}
                    {sectionCounters.optional.total}
                </strong></p>
                {optionalJobs.length > 0 &&
                    <div className="card">
                        {optionalJobs.map(job => <FollowJob job={job}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[3894]}</p>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

export const FollowSkills = ({sectionCounters}) => {
    const rm = useSelector(selectSelectedRm);
    const cv = useSelector(selectCv);
    const {languages, competences, materials} = rmBgItemsAsArray(rm);
    const mandatoryLanguages = mandatoryItems(languages);
    const optionalLanguages = optionalItems(languages);
    const mandatoryCompetences = mandatoryItems(competences);
    const optionalCompetences = optionalItems(competences);
    const mandatoryMaterials = mandatoryItems(materials);
    const optionalMaterials = optionalItems(materials);
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const onFeedback = (materialId, materialPath, competence = null) => {
        if (!materialId || !materialPath) return;

        if ((cv?.bg?.materials || []).includes(materialId)) {
            dispatch(openAlert({type: "warning", message: t("alert_cv_already_updated")}))
            return;
        }

        dispatch(openDialog({
            Content: RmMaterialFeedback,
            title: t("student_follow_material_feedback_title"),
            props: {
                materialId,
                materialPath,
                competence
            },
            closable: true
        }));
    }

    return (
        <div className="followSkills">
            <div className="mandatoryCol">
                <p className="counter">{t("student_follow_mandatory") + " "}<strong>
                    {sectionCounters.mandatory.done}{"/"}
                    {sectionCounters.mandatory.total}
                </strong></p>
                {mandatoryLanguages.length > 0 &&
                    <div className="card">
                        {mandatoryLanguages.map(language => <FollowLanguage language={language}
                                                                            onFeedback={onFeedback}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[419]}</p>
                        </div>
                    </div>
                }
                {mandatoryCompetences.length > 0 &&
                    <div className="card">
                        {mandatoryCompetences.map(competence => <FollowCompetence competence={competence}
                                                                                  onFeedback={onFeedback}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[3938]}</p>
                        </div>
                    </div>
                }
                {mandatoryMaterials.length > 0 &&
                    <div className="card">
                        {mandatoryMaterials.sort((a, b) => (b.title > a.title ? -1 : 1)).map(material => <FollowMaterial
                            material={material}
                            onFeedback={onFeedback}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[3910]}</p>
                        </div>
                    </div>
                }
            </div>
            <div className="optionalCol">
                <p className="counter">{t("student_follow_optionals") + " "}<strong>
                    {sectionCounters.optional.done}{"/"}
                    {sectionCounters.optional.total}
                </strong></p>
                {optionalLanguages.length > 0 &&
                    <div className="card">
                        {optionalLanguages.map(language => <FollowLanguage language={language}
                                                                           onFeedback={onFeedback}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[419]}</p>
                        </div>
                    </div>
                }
                {optionalCompetences.length > 0 &&
                    <div className="card">
                        {optionalCompetences.map(competence => <FollowCompetence competence={competence}
                                                                                 onFeedback={onFeedback}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[3938]}</p>
                        </div>
                    </div>
                }
                {optionalMaterials.length > 0 &&
                    <div className="card">
                        {optionalMaterials.sort((a, b) => (b.title > a.title ? -1 : 1)).map(material => <FollowMaterial material={material}
                                                                           onFeedback={onFeedback}/>)}
                        <div className="card__footer">
                            <p className="tag">{emojiCompact[3910]}</p>
                        </div>
                    </div>
                }
            </div>
        </div>
    )
}

const FollowQualification = ({qualification}) => {
    const {t} = useTranslation();
    const scopeIds = qualification?.scopeIds || [];
    const level = t(typeIdToTypeLabel(qualification?.typeId));
    const description = qualification?.description || "";
    const label = qualification?.label || "";
    const optional = qualification?.importance === 2 ? true : false;
    const title = scopeIds.map((id, i) => t(datasetIdToLabel(qualificationScopes, id)) + (i !== scopeIds.length - 1 ? " | " : ""));
    const cv = useSelector(selectCv);

    const color = number => {
        if (number === -1) return "grey";
        if (number === 0) return "r";
        if (number === 1) return "y";
        if (number === 2) return "g";
    }

    return <div key={qualification.id}
                className={`card__item card__item--${color(hasQualification(cv, qualification))}`}>
        <div className="card__item__header">
            <h2 className="title">{title}</h2>
        </div>
        <div className="card__item__content">
            <p className="light">{level}</p>
            {description && <p className="regular">{description}</p>}
            {label && <>
                <label>{t("student_follow_qual_suggested")}</label>
                <p>{label}</p>
            </>}
            {optional && <p>{t("student_follow_optional")}</p>}
        </div>
    </div>
}

const FollowJob = ({job}) => {
    const {t} = useTranslation();
    const scopeIds = job?.scopeIds ? job.scopeIds :
        job?.scopeId != null ? [job.scopeId] : [];
    const title = scopeIds.map((id, i) => t(professionScopes[id].label) + (i !== scopeIds.length - 1 ? " | " : ""));
    const months = job?.months || null;
    const contract = job?.contractId != null ? t(jobContracts[job.contractId].label) : "";
    const subtitle = contract + (months ? ` | ${months} mesi` : "");
    const desc = job?.description || "";
    const jobTitles = job?.labels || [];
    const optional = job?.importance === 2 ? true : false;
    const cv = useSelector(selectCv);

    const color = number => {
        if (number === -1) return "grey";
        if (number === 0) return "r";
        if (number === 1) return "y";
        if (number === 2) return "g";
    }

    return <div className={`card__item card__item--${color(hasJob(cv, job))}`}>
        <div className="card__item__header">
            <h2 className="title">{title}</h2>
        </div>
        <div className="card__item__content">
            <p className="light">{subtitle}</p>
            <p className="regular">{desc}</p>
            {jobTitles.length > 0 && <>
                <label>{t("student_follow_job_suggested")}</label>
                <div className="suggestedJobList">{jobTitles.map(label => <div className="suggestedJob">{label}</div>)}</div>
            </>}
            {optional && <p>{t("student_follow_optional")}</p>}
        </div>
    </div>
}

const FollowLanguage = ({language, onFeedback}) => {
    const materials = Object.values(language?.materials || {});
    const title = language?.labelId != null ? languages[language.labelId].label : "";
    const level = language?.levelId != null ? languageLevels[language.levelId].label : "";
    const certificate = language?.certificate || "";
    const optional = language?.importance === 2 ? true : false;
    const cv = useSelector(selectCv);
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const showFeedback = (material) => {
        return !(cv?.bg?.materials || []).includes(material);
    };

    const onLinkClick = url => {
        dispatch(openDialog({
            Content: CourseReminder,
            props: {url},
            title: t("student_follow_courseReminder_title"),
            closable: true
        }))
    }

    const color = number => {
        if (number === -1) return "grey";
        if (number === 0) return "r";
        if (number === 1) return "y";
        if (number === 2) return "g";
    }

    return <div className={`card__item card__item--${color(hasLanguage(cv, language))}`}>
        <div className="card__item__header">
            <h2 className="title">{title}</h2>
        </div>
        <div className="card__item__content">
            <p className="bold">{level}</p>
            {optional && <p>{t("student_follow_optional")}</p>}
            <p className="regular">{certificate}</p>
            {materials.length > 0 && <>
                <hr/>
                {materials.map(mat =>
                    <div key={mat.id} className="mat">
                        <p>{mat.title}</p>
                        <div className="actions">
                            <Button
                                onClick={() => onLinkClick(mat.link)}>{t("student_follow_startCourse")}</Button>
                            {showFeedback(mat) && <Tooltip title={t("student_follow_course_prove")}>
                                <ButtonCircle
                                    onClick={() => onFeedback(mat.id, `bg.languages.${language.id}.materials.${mat.id}`)}><InsertCommentIcon/></ButtonCircle>
                            </Tooltip>}
                        </div>
                    </div>
                )}
            </>}
        </div>
    </div>
}

const FollowCompetence = ({competence, onFeedback}) => {
    const {t} = useTranslation();
    const materials = Object.values(competence?.materials || {});
    const title = competence?.labelId != null ? competences[competence.labelId].label : "";
    const level = competence?.levelId != null ? t(competenceLevels[competence.levelId].label) : "";
    const certificate = competence?.certificate || "";
    const optional = competence?.importance === 2 ? true : false;
    const cv = useSelector(selectCv);
    const dispatch = useDispatch();

    const showFeedback = (material) => {
        return !(cv?.bg?.materials || []).includes(material);
    };

    const onLinkClick = url => {
        console.log(url)
        dispatch(openDialog({
            Content: CourseReminder,
            title: t("student_follow_courseReminder_title"),
            props: {url},
            closable: true
        }))
    }

    const color = number => {
        if (number === -1) return "grey";
        if (number === 0) return "r";
        if (number === 1) return "y";
        if (number === 2) return "g";
    }

    return <div className={`card__item card__item--${color(hasCompetence(cv, competence))}`}>
        <div className="card__item__header">
            <h2 className="title">{title}</h2>
        </div>
        <div className="card__item__content">
            <p className="bold">{level}</p>
            {optional && <p>{t("student_follow_optional")}</p>}
            <p className="regular">{certificate}</p>
            {materials.length > 0 && <>
                <hr/>
                {materials.map(mat =>
                    <div key={mat.id} className="mat">
                        <p>{mat.title}</p>
                        <div className="actions">
                            <Button
                                onClick={() => onLinkClick(mat.link)}>{t("student_follow_startCourse")}</Button>
                            {showFeedback(mat) && <Tooltip title={t("student_follow_course_prove")}>
                                <ButtonCircle
                                    onClick={() => onFeedback(mat.id, `bg.competences.${competence.id}.materials.${mat.id}`, competence)}><InsertCommentIcon/></ButtonCircle>
                            </Tooltip>}
                        </div>
                    </div>
                )}
            </>}
        </div>
    </div>
}

const FollowMaterial = ({material, onFeedback}) => {
    const title = material?.title;
    const description = material?.description;
    const link = material?.link;
    const cv = useSelector(selectCv);
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const showFeedback = (material) => {
        return !(cv?.bg?.materials || []).includes(material);
    };

    const onLinkClick = url => {
        console.log(url)

        dispatch(openDialog({
            Content: CourseReminder,
            title: t("student_follow_courseReminder_title"),
            props: {url},
            closable: true
        }))
    }

    const color01 = number => {
        if (number === 0) return "r";
        if (number === 1) return "g";
    }

    return <div className={`card__item card__item--${color01(hasMaterial(cv, material))}`}>
        <div className="card__item__header">
            <h2 className="title">{title}</h2>
        </div>
        <div className="card__item__content">
            <p>{description}</p>
            <div className="actions">
                <Button onClick={() => onLinkClick(link)}>{t("student_follow_startCourse")}</Button>
                {showFeedback(material) && <Tooltip title={t("student_follow_course_prove")}>
                    <ButtonCircle
                        onClick={() => onFeedback(material.id, `bg.materials.${material.id}`)}><InsertCommentIcon/></ButtonCircle>
                </Tooltip>}
            </div>
        </div>
    </div>
}